// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useEffect, useId, useRef, useState } from 'react';

import type { AuthUser } from '../api/types';
import { GLOBAL_ROLE_LABELS, globalRoleOf, initials } from '../app/auth';
import { ADMIN_USERS_PATH, GROUPS_PATH, KONTO_PATH } from '../app/cases';
import { ApiTokens } from './ApiTokens';
import styles from './UserMenu.module.css';

export interface UserMenuProps {
  user: AuthUser;
  /** Signs out of ruxd and, with Supabase, of Supabase too; then the login page. */
  onLogout: () => void;
  /** Signed in through a share link: read-only, no account of its own. */
  viaShareLink?: boolean;
}

/**
 * Who is signed in, at the right end of the Sager bar and the Bygning top bar:
 * initials and name (initials only on a phone); open it for the email and
 * "Log ud" — the panel drops down, anchored to the button's right edge so it
 * stays on screen. Server mode only — local mode has no login.
 */
export function UserMenu({ user, onLogout, viaShareLink = false }: UserMenuProps) {
  const [open, setOpen] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelId = useId();
  // A share link with no label has neither name nor e-mail.
  const name = user.display_name || user.email || (viaShareLink ? 'Delt link' : '?');
  const role = globalRoleOf(user);

  // Escape or a click elsewhere closes it; Escape returns focus to the button.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      setOpen(false);
      buttonRef.current?.focus();
    };
    const onPointer = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPointer);
    };
  }, [open]);

  return (
    <div ref={rootRef} className={styles.root}>
      <button
        ref={buttonRef}
        type="button"
        className={styles.button}
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={`Bruger: ${name}`}
        onClick={() => setOpen((o) => !o)}
      >
        <span className={styles.avatar} aria-hidden="true">
          {initials(name)}
        </span>
      </button>
      {open && (
        <div id={panelId} className={styles.panel} role="group" aria-label="Bruger">
          <div className={styles.who}>
            <span className={styles.whoName}>{name}</span>
            <span className={styles.whoEmail}>{viaShareLink ? 'Delt link · kun læseadgang' : user.email}</span>
            {role !== 'user' && <span className={styles.badge}>{GLOBAL_ROLE_LABELS[role]}</span>}
          </div>
          {!viaShareLink && (
            <nav className={styles.links} aria-label="Konto">
              {/* Plain links: each is its own page load, outside any case. */}
              <a className={styles.link} href={KONTO_PATH}>
                Konto
              </a>
              <a className={styles.link} href={GROUPS_PATH}>
                Grupper
              </a>
              {role === 'admin' && (
                <a className={styles.link} href={ADMIN_USERS_PATH}>
                  Brugere og roller
                </a>
              )}
            </nav>
          )}
          {!viaShareLink && <ApiTokens />}
          <button
            type="button"
            className={styles.logout}
            disabled={leaving}
            onClick={() => {
              setLeaving(true);
              onLogout();
            }}
          >
            {leaving ? 'Logger ud…' : viaShareLink ? 'Luk delt link' : 'Log ud'}
          </button>
        </div>
      )}
    </div>
  );
}
